import { useContext, useEffect, useMemo, useState } from 'react';
import { UserDetailsContext } from '../context/user-details/user-details';
import { ReportPeriodContext } from '../context/report-periods/report-periods';
import ReportPeriodService from '../shared/services/report-period/report-period.service';
import LocationService from '../shared/services/location/location.service';
import UserDetailsService from '../shared/services/user-details/user-details.service';
import EmissionService from '../shared/services/emission/emission.service';

export type GetStartedStepId =
  | 'company-details'
  | 'reporting-period'
  | 'first-location'
  | 'invite-colleague'
  | 'first-emission';

export interface IGetStartedStepDefinition {
  id: GetStartedStepId;
  label: string;
  description: string;
  buttonLabel: string;
  /**
   * Where the step's button lands. The reporting-period step is normally
   * overridden by the caller to open the existing start-period dialog instead.
   */
  path: string;
}

/**
 * The single source of truth for the onboarding steps. The dashboard's
 * Get-started card and the first-run Welcome dialog both render from this list
 * so the two can never drift apart.
 */
export const GET_STARTED_STEPS: IGetStartedStepDefinition[] = [
  {
    id: 'company-details',
    label: 'Add your company details',
    description: 'Set your company name and how you report.',
    buttonLabel: 'Add company details',
    path: '/settings/company-settings/overview',
  },
  {
    id: 'reporting-period',
    label: 'Start a reporting period',
    description: "Set the dates you'll measure and report against.",
    buttonLabel: 'Start a period',
    path: '/settings/company-settings/reporting-periods',
  },
  {
    id: 'first-location',
    label: 'Add your first location',
    description: 'Add the office or site where you operate.',
    buttonLabel: 'Add a location',
    path: '/organisation/locations',
  },
  {
    id: 'invite-colleague',
    label: 'Invite a colleague',
    description: 'Share the workload with your team.',
    buttonLabel: 'Invite someone',
    path: '/settings/company-settings/users',
  },
  {
    id: 'first-emission',
    label: 'Record your first emission',
    description: 'Enter activity data or import it from a spreadsheet.',
    buttonLabel: 'Record emissions',
    path: '/emissions/overview',
  },
];

export interface IGetStartedStep extends IGetStartedStepDefinition {
  done: boolean;
  /** Only the invite step is individually skippable. */
  skipped: boolean;
}

const hiddenStorageKey = (organisationId: string) => `getStartedGuideHidden:${organisationId}`;
const inviteSkippedStorageKey = (organisationId: string) => `getStartedInviteSkipped:${organisationId}`;
const completeStorageKey = (organisationId: string) => `getStartedGuideComplete:${organisationId}`;

/**
 * Derives the dashboard Get-started guide from REAL state on every load, not
 * navigation state: company name from the session, reporting periods from the
 * shared context (confirmed with a fresh fetch when it reads empty, because the
 * context's list is briefly stale straight after login), locations and users
 * from their list endpoints, and "any emission recorded" from the emission
 * list's count (one row requested, so it is as cheap as the check gets).
 *
 * Dismissal is persisted per organisation in localStorage and never blocks the
 * guide from being reopened while steps remain.
 */
export const useGetStartedSteps = (enabled: boolean) => {
  const userDetails = useContext(UserDetailsContext).userDetails;
  const reportPeriodContext = useContext(ReportPeriodContext);
  const organisationId = userDetails?.organisation?.id;

  const [hidden, setHidden] = useState(true);
  const [inviteSkipped, setInviteSkipped] = useState(false);
  // Once every step has been seen done for this org, this flag short-circuits
  // the fetches on later mounts so a fully set-up org stops paying for checks.
  const [complete, setComplete] = useState(true);
  const [flagsRead, setFlagsRead] = useState(false);
  const [hasPeriod, setHasPeriod] = useState<boolean | null>(null);
  const [periodIdForEmissionCheck, setPeriodIdForEmissionCheck] = useState<string | null>(null);
  const [hasLocation, setHasLocation] = useState<boolean | null>(null);
  const [hasColleague, setHasColleague] = useState<boolean | null>(null);
  const [hasEmission, setHasEmission] = useState<boolean | null>(null);

  // Persisted flags.
  useEffect(() => {
    if (!organisationId) return;
    setHidden(localStorage.getItem(hiddenStorageKey(organisationId)) === 'true');
    setInviteSkipped(localStorage.getItem(inviteSkippedStorageKey(organisationId)) === 'true');
    setComplete(localStorage.getItem(completeStorageKey(organisationId)) === 'true');
    setFlagsRead(true);
  }, [organisationId]);

  const shouldFetch = enabled && flagsRead && !complete;

  // Reporting periods. The shared context is authoritative once populated; an
  // empty list is confirmed with a fresh fetch (see use-show-welcome-dialog for
  // why the context can momentarily read "loaded + empty" for a user who has
  // periods).
  useEffect(() => {
    if (!shouldFetch) return;
    if (reportPeriodContext.allReportPeriods.length > 0) {
      setHasPeriod(true);
      setPeriodIdForEmissionCheck(reportPeriodContext.lastSelectedReportId || reportPeriodContext.allReportPeriods[0].id);
      return;
    }
    new ReportPeriodService().getAllReportPeriod()
      .then((periods) => {
        setHasPeriod(periods.length > 0);
        setPeriodIdForEmissionCheck(periods[0]?.id || null);
      })
      .catch(() => {
        // Fail towards done so an API blip never nags a set-up org.
        setHasPeriod(true);
        setPeriodIdForEmissionCheck(null);
      });
  }, [shouldFetch, reportPeriodContext.allReportPeriods, reportPeriodContext.lastSelectedReportId]);

  // Locations.
  useEffect(() => {
    if (!shouldFetch) return;
    new LocationService().getAllLocations()
      .then((locations) => setHasLocation(locations.length > 0))
      .catch(() => setHasLocation(true));
  }, [shouldFetch, organisationId]);

  // Colleagues: any second non-deactivated user. Fetched WITHOUT the BE's
  // status filter, because `?status=active` excludes invited-not-yet-accepted
  // users and a pending invite must count: the inviting is the step.
  useEffect(() => {
    if (!shouldFetch) return;
    new UserDetailsService().getAllUserDetails()
      .then((users) => setHasColleague(users.filter((user) => user.active).length > 1))
      .catch(() => setHasColleague(true));
  }, [shouldFetch, organisationId]);

  // First emission: the emission list's count for the current period, one row.
  useEffect(() => {
    if (!shouldFetch) return;
    if (hasPeriod === null) return;
    if (!hasPeriod) {
      // Emission records live inside a reporting period, so no period means none.
      setHasEmission(false);
      return;
    }
    if (!periodIdForEmissionCheck) {
      // Only the period fetch's error path leaves a period without an id, so
      // fail towards done here too rather than nag on an API blip.
      setHasEmission(true);
      return;
    }
    new EmissionService().getAll(periodIdForEmissionCheck, 0, 1)
      .then((response) => setHasEmission(response.count > 0))
      .catch(() => setHasEmission(true));
  }, [shouldFetch, hasPeriod, periodIdForEmissionCheck]);

  const companyDone = userDetails?.organisation?.name != null;

  const steps: IGetStartedStep[] = useMemo(() => GET_STARTED_STEPS.map((definition) => {
    const done =
      definition.id === 'company-details' ? companyDone :
        definition.id === 'reporting-period' ? hasPeriod === true :
          definition.id === 'first-location' ? hasLocation === true :
            definition.id === 'invite-colleague' ? hasColleague === true :
              hasEmission === true;
    return {
      ...definition,
      done,
      skipped: definition.id === 'invite-colleague' && inviteSkipped,
    };
  }), [companyDone, hasPeriod, hasLocation, hasColleague, hasEmission, inviteSkipped]);

  const loaded = enabled && flagsRead
    && (complete
      || (hasPeriod !== null && hasLocation !== null && hasColleague !== null && hasEmission !== null));
  const allDone = complete || steps.every((step) => step.done || step.skipped);

  // Remember a fully completed guide per org so later mounts skip the fetches.
  useEffect(() => {
    if (!enabled || !loaded || complete || !allDone || !organisationId) return;
    localStorage.setItem(completeStorageKey(organisationId), 'true');
    setComplete(true);
  }, [enabled, loaded, complete, allDone, organisationId]);

  /** Render the card. */
  const visible = loaded && !allDone && !hidden;
  /** Steps remain but the card was dismissed: offer the small reopen link. */
  const reopenAvailable = loaded && !allDone && hidden;

  const hide = () => {
    if (organisationId) localStorage.setItem(hiddenStorageKey(organisationId), 'true');
    setHidden(true);
  };
  const show = () => {
    if (organisationId) localStorage.removeItem(hiddenStorageKey(organisationId));
    setHidden(false);
  };
  const skipInvite = () => {
    if (organisationId) localStorage.setItem(inviteSkippedStorageKey(organisationId), 'true');
    setInviteSkipped(true);
  };

  return { steps, loaded, allDone, visible, reopenAvailable, hide, show, skipInvite };
};
